import { useState, type FormEvent } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ArrowLeft, Loader2 } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface EventData {
  name: string
  eventDate: string | null
  eventTime: string | null
  videoLink: string
  about: string
  location: string
  primaryColor: string
  secondaryColor: string
  eventUserTypes: string
  ended: boolean
}

interface Props extends Omit<AppShellProps, 'children'> {
  event: EventData
  errors: Record<string, string>
  urls: AppShellProps['urls'] & {
    create: string
  }
  translations: AppShellProps['translations'] & {
    newEvent: string
    eventName: string
    eventDate: string
    eventTime: string
    logo: string
    videoLink: string
    aboutTheEvent: string
    eventLocation: string
    primaryColor: string
    secondaryColor: string
    userTypes: string
    userTypesPlaceholder: string
    ended: string
    submit: string
    back: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function New(props: Props) {
  const { event, errors, urls, translations: t, ...shell } = props

  const [form, setForm] = useState({
    name: event.name,
    event_date: event.eventDate || '',
    event_time: event.eventTime || '',
    video_link: event.videoLink,
    about: event.about,
    location: event.location,
    primary_color: event.primaryColor,
    secondary_color: event.secondaryColor,
    event_user_types: event.eventUserTypes,
    ended: event.ended,
  })
  const [logo, setLogo] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)

  function set(field: string, value: string | boolean) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)

    const fd = new FormData()
    fd.append('event[name]', form.name)
    fd.append('event[event_date]', form.event_date)
    fd.append('event[event_time]', form.event_time)
    fd.append('event[video_link]', form.video_link)
    fd.append('event[about]', form.about)
    fd.append('event[location]', form.location)
    fd.append('event[primary_color]', form.primary_color)
    fd.append('event[secondary_color]', form.secondary_color)
    fd.append('event[event_user_types]', form.event_user_types)
    fd.append('event[ended]', form.ended ? '1' : '0')
    fd.append('kind', 'event')
    if (logo) fd.append('event[logo]', logo)

    router.post(urls.create, fd, {
      forceFormData: true,
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={t.newEvent} />

      <div className="mx-auto max-w-2xl py-8 px-4">
        <Button
          variant="ghost"
          size="sm"
          className="mb-6 text-[#91918E]"
          onClick={() => router.visit(urls.events)}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t.back}
        </Button>

        <h1 className="text-xl font-semibold text-[#37352F] mb-6">{t.newEvent}</h1>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Event Name */}
          <Field label={t.eventName} error={errors.name}>
            <Input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              className="border-[#E8E8E5]"
            />
          </Field>

          {/* Date + Time */}
          <div className="grid grid-cols-2 gap-4">
            <Field label={t.eventDate} error={errors.event_date}>
              <Input
                type="date"
                value={form.event_date}
                onChange={(e) => set('event_date', e.target.value)}
                className="border-[#E8E8E5]"
              />
            </Field>
            <Field label={t.eventTime} error={errors.event_time}>
              <Input
                type="time"
                value={form.event_time}
                onChange={(e) => set('event_time', e.target.value)}
                className="border-[#E8E8E5]"
              />
            </Field>
          </div>

          {/* Logo */}
          <Field label={t.logo}>
            <Input
              type="file"
              accept="image/jpeg,image/png"
              onChange={(e) => setLogo(e.target.files?.[0] || null)}
              className="border-[#E8E8E5]"
            />
          </Field>

          {/* Video Link */}
          <Field label={t.videoLink}>
            <Input
              value={form.video_link}
              onChange={(e) => set('video_link', e.target.value)}
              className="border-[#E8E8E5]"
            />
          </Field>

          {/* About */}
          <Field label={t.aboutTheEvent} error={errors.about}>
            <Input
              value={form.about}
              onChange={(e) => set('about', e.target.value)}
              className="border-[#E8E8E5]"
            />
          </Field>

          {/* Location */}
          <Field label={t.eventLocation} error={errors.location}>
            <Input
              value={form.location}
              onChange={(e) => set('location', e.target.value)}
              className="border-[#E8E8E5]"
            />
          </Field>

          {/* Colors */}
          <div className="grid grid-cols-2 gap-4">
            <Field label={t.primaryColor}>
              <Input
                type="color"
                value={form.primary_color}
                onChange={(e) => set('primary_color', e.target.value)}
                className="h-10 border-[#E8E8E5]"
              />
            </Field>
            <Field label={t.secondaryColor}>
              <Input
                type="color"
                value={form.secondary_color}
                onChange={(e) => set('secondary_color', e.target.value)}
                className="h-10 border-[#E8E8E5]"
              />
            </Field>
          </div>

          {/* User Types */}
          <Field label={t.userTypes}>
            <textarea
              value={form.event_user_types}
              onChange={(e) => set('event_user_types', e.target.value)}
              rows={5}
              placeholder={t.userTypesPlaceholder}
              className="w-full rounded-lg border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
            />
          </Field>

          {/* Ended */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.ended}
              onChange={(e) => set('ended', e.target.checked)}
              className="h-4 w-4 rounded border-[#E8E8E5] text-[#2383E2] focus:ring-[#2383E2]"
            />
            <span className="text-sm text-[#37352F]">{t.ended}</span>
          </label>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#2383E2] hover:bg-[#1b6ec2]"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t.submit}
          </Button>
        </form>
      </div>
    </AppShell>
  )
}

/* ------------------------------------------------------------------ */
/*  Field wrapper                                                      */
/* ------------------------------------------------------------------ */

function Field({
  label,
  error,
  children,
}: {
  label: string
  error?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <Label className="mb-1.5 text-sm text-[#37352F]">{label}</Label>
      {children}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  )
}
